/**
 * usePwaDetection Composable
 *
 * @version 1.1.0
 * @description Detects PWA standalone mode and manages dynamic `<meta name="theme-color">`.
 *              Used by mobile shell components to adapt behavior between browser and installed PWA.
 *
 * FEATURES:
 * - Detects `display-mode: standalone` (installed PWA)
 * - Detects iOS standalone via `navigator.standalone`
 * - Dynamically updates `<meta name="theme-color">` for status bar coloring
 * - Reactive: listens for display-mode changes (e.g., user installs PWA mid-session)
 *
 * IMPORTANT: isStandalone is computed synchronously in setup phase (not deferred
 * to onMounted) so lazy-loaded components get the correct value on first render.
 *
 * USAGE:
 *   const { isStandalone, setThemeColor } = usePwaDetection()
 *
 *   // React to area changes
 *   watch(currentArea, (area) => {
 *     setThemeColor(AREA_SECTION_COLORS[area] ?? '#232F34')
 *   })
 */

import { ref, onMounted, onUnmounted, type Ref } from 'vue'

// =============================================================================
// Types
// =============================================================================

export interface UsePwaDetectionReturn {
  /** true when running as installed PWA (display-mode: standalone) */
  readonly isStandalone: Ref<boolean>
  /** Dynamically set the <meta name="theme-color"> content */
  setThemeColor: (color: string) => void
}

// =============================================================================
// Module-level detection (runs once, synchronously, before any component mounts)
// =============================================================================

function _checkStandalone(): boolean {
  if (typeof window === 'undefined') return false
  if (window.matchMedia('(display-mode: standalone)').matches) return true
  if ((window.navigator as any).standalone === true) return true
  if (typeof document !== 'undefined' && document.referrer.includes('android-app://')) return true
  return false
}

// =============================================================================
// Composable
// =============================================================================

export function usePwaDetection(): UsePwaDetectionReturn {
  // Synchronous — correct on first render, even for lazy-loaded components
  const isStandalone = ref(_checkStandalone())

  let standaloneMediaQuery: MediaQueryList | null = null

  // ── Detection ────────────────────────────────────────────────────────────

  function handleStandaloneChange(e: MediaQueryListEvent): void {
    isStandalone.value = e.matches || (window.navigator as any).standalone === true
  }

  // ── Theme Color ──────────────────────────────────────────────────────────

  /**
   * Dynamically update <meta name="theme-color"> to color the status bar
   * on Android Chrome and PWA standalone mode.
   */
  function setThemeColor(color: string): void {
    if (typeof document === 'undefined') return

    let meta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null
    if (!meta) {
      meta = document.createElement('meta')
      meta.name = 'theme-color'
      document.head.appendChild(meta)
    }
    meta.content = color
  }

  // ── Lifecycle ────────────────────────────────────────────────────────────

  onMounted(() => {
    // Listen for future changes (e.g., user installs PWA mid-session)
    if (typeof window !== 'undefined' && window.matchMedia) {
      standaloneMediaQuery = window.matchMedia('(display-mode: standalone)')
      standaloneMediaQuery.addEventListener('change', handleStandaloneChange)
    }
  })

  onUnmounted(() => {
    if (standaloneMediaQuery) {
      standaloneMediaQuery.removeEventListener('change', handleStandaloneChange)
      standaloneMediaQuery = null
    }
  })

  return {
    isStandalone,
    setThemeColor,
  }
}

export default usePwaDetection
